import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getMessages, getSession } from '../services/sessions';
import { SESSION_LABELS } from '../utils/sessionDisplay';
import type { Session, SessionMessage } from '../types';

function formatDateTime(date: Date): string {
  return date.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDuration(durationSeconds?: number): string | null {
  if (!durationSeconds || durationSeconds <= 0) return null;
  if (durationSeconds < 60) return '<1 min';
  return `${Math.round(durationSeconds / 60)} min`;
}

function getSessionDate(session: Session): Date {
  if (session.startedAt?.toDate) return session.startedAt.toDate();
  return new Date((session.startedAt as unknown as { seconds: number }).seconds * 1000);
}

function getSessionTitle(session: Session): string {
  const title = session.outputs?.sessionTitle?.trim();
  if (title) return title;

  const taskTitle = session.outputs?.taskTitle?.trim();
  if (taskTitle) return taskTitle;

  return SESSION_LABELS[session.type];
}

function getContinueMode(session: Session): 'voice' | 'text' {
  // Preserve the original conversation modality when resuming.
  return session.modality === 'text' ? 'text' : 'voice';
}

export default function ConversationDetailPage() {
  const { user } = useAuth();
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<SessionMessage[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!user?.uid || !sessionId) {
        setLoading(false);
        setError('Conversation not found');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const [sessionData, messageData] = await Promise.all([
          getSession(user.uid, sessionId),
          getMessages(user.uid, sessionId),
        ]);

        if (cancelled) return;
        if (!sessionData) {
          setError('Conversation not found');
          setSession(null);
          setMessages([]);
          return;
        }

        setSession(sessionData);
        setMessages(messageData);
      } catch {
        if (!cancelled) {
          setError('Failed to load conversation');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, sessionId]);

  return (
    <div className="min-h-screen bg-[var(--color-background)] flex flex-col">
      <header className="px-5 pt-8 pb-4 flex items-start justify-between gap-3 border-b border-[var(--color-border)]">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors shrink-0"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-[var(--color-text-primary)] truncate">
              {session ? getSessionTitle(session) : 'Conversation'}
            </h1>
            {session && (
              <p className="text-xs text-[var(--color-text-muted)] mt-1">
                {formatDateTime(getSessionDate(session))}
              </p>
            )}
          </div>
        </div>

        {session && (
          <button
            onClick={() => navigate(`/session?type=${session.type}&mode=${getContinueMode(session)}&continue=${session.id}`)}
            className="flank-btn flank-btn-primary shrink-0"
          >
            Continue
          </button>
        )}
      </header>

      {loading && (
        <div className="py-12 flex justify-center">
          <div className="w-6 h-6 rounded-full animate-pulse" style={{ background: 'var(--color-surface)' }} />
        </div>
      )}

      {!loading && error && (
        <div className="px-5 py-10 text-center">
          <p className="text-sm text-[var(--color-text-muted)]">{error}</p>
        </div>
      )}

      {!loading && session && !error && (
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="px-5 py-3 border-b border-[var(--color-border)]">
            <div className="w-full max-w-4xl mx-auto">
              <p className="text-xs text-[var(--color-text-secondary)] uppercase tracking-wider">
                {formatDuration(session.durationSeconds) ?? 'Duration unavailable'}
                <span className="mx-2">•</span>
                {session.messageCount} {session.messageCount === 1 ? 'message' : 'messages'}
              </p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            <div className="w-full max-w-4xl mx-auto flex flex-col gap-4">
              {messages.length === 0 && (
                <p className="text-sm text-[var(--color-text-muted)] text-center py-8">
                  No transcript messages available for this conversation.
                </p>
              )}
              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ message }: { message: SessionMessage }) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
          isUser ? 'rounded-br-sm' : 'rounded-bl-sm'
        }`}
        style={
          isUser
            ? { background: 'var(--color-brand-primary)', color: 'var(--flank-dark-blue)' }
            : { background: 'var(--color-surface)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)' }
        }
      >
        {message.content}
      </div>
    </div>
  );
}
